import type { CSSProperties } from "react";
import Image from "next/image";
import Link from "next/link";
import type { DownloadPlatform } from "@/lib/data/downloadPlatforms";
import type { PlatformRelease } from "@/lib/data/playerReleases";
import { CopyPathButton } from "./CopyPathButton";

interface PlatformCardProps {
  platform: DownloadPlatform;
  /** Live release for this platform, pulled from releases.json. */
  release?: PlatformRelease;
  /** Position in the grid - the first few icons are eagerly loaded. */
  index: number;
  /** CDN base path for the player icons. */
  baseUrl: string;
}

/** Platforms whose spec row reads better with a handheld icon than a display. */
const HANDHELD_PLATFORMS = new Set(["android", "iOS"]);

const DownloadArrow = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="flex-shrink-0">
    <path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ChevronRight = ({ className }: { className?: string }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="6" y="2" width="12" height="20" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
    <path d="M11 18.5h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const MonitorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="2.5" y="4" width="19" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M9 20.5h6M12 17.5v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/** Versions carry a mix of "7.2.0" and "v7.2.0" - display them consistently. */
const formatVersion = (version: string) =>
  version.toLowerCase().startsWith("v") ? version : `v${version}`;

/** Name the artefact on the button when the URL makes it unambiguous. */
const FILE_TYPE_LABELS: Record<string, string> = {
  apk: "APK",
  exe: "EXE",
  deb: "DEB",
  ipk: "IPK",
  dmg: "DMG",
  gz: "IMG",
};

const downloadLabel = (url: string) => {
  const extension = url.split("?")[0].split(".").pop()?.toLowerCase() ?? "";
  const fileType = FILE_TYPE_LABELS[extension];
  return fileType ? `Download ${fileType}` : "Download";
};

export function PlatformCard({ platform, release, index, baseUrl }: PlatformCardProps) {
  const isPriority = index < 3;
  const brandColor = platform.brandColor ?? "var(--color-accent, #e11d48)";
  const SpecIcon = HANDHELD_PLATFORMS.has(platform.id) ? PhoneIcon : MonitorIcon;
  const spec = platform.downloadItems[0];
  /**
   * releases.json drives the download when it carries one for this platform;
   * otherwise the bundled versions stand in (Android, Fire TV, ... have no
   * download path in the manifest).
   */
  const downloads = release?.downloadUrl
    ? [{ url: release.downloadUrl, version: release.version, label: "latest release" }]
    : (spec?.versions ?? []).map((version) => ({
        url: version.downloadUrl,
        version: version.version,
        label: version.label,
      }));

  return (
    <article
      id={platform.id}
      className="group/card scroll-mt-24 flex flex-col rounded-2xl border border-gray-200 bg-white p-7 shadow-sm sm:p-8 transition-shadow hover:shadow-md"
    >
      {/* Header */}
      <div className="flex items-center gap-4">
        <Image
          src={`${baseUrl}/${platform.icon}`}
          width={48}
          height={48}
          priority={isPriority}
          fetchPriority={isPriority ? "high" : "auto"}
          className="h-12 w-12 flex-shrink-0 object-contain"
          alt={platform.name}
        />
        <h3 className="text-xl font-bold leading-tight text-gray-900 xl:text-2xl">
          {platform.name}
        </h3>
      </div>

      {platform.description && (
        <p className="mt-5 text-[15px] leading-[1.7] text-gray-600">{platform.description}</p>
      )}

      {/* Release block - compatibility spec plus the live download from releases.json */}
      {(spec || downloads.length > 0) && (
        <div className="mt-7 space-y-4 border-t border-gray-100 pt-6">
          {spec && (
            <div className="flex items-start gap-3.5">
              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500">
                <SpecIcon />
              </span>
              <div className="min-w-0">
                <p className="text-sm text-gray-500">{spec.label}</p>
                <p className="text-[15px] font-semibold text-gray-900">{spec.value}</p>
              </div>
            </div>
          )}

          {downloads.length > 0 && (
            <div className="space-y-2">
              {downloads.map((download) => (
                <Link
                  key={download.url}
                  href={download.url}
                  aria-label={`${platform.name} - ${download.label}: ${formatVersion(download.version)}`}
                  style={{ "--brand": brandColor } as CSSProperties}
                  className="flex h-14 items-center gap-3 rounded-xl border border-primary px-5 text-primary transition-colors group-hover/card:border-[var(--brand)] group-hover/card:bg-[var(--brand)] group-hover/card:text-white"
                >
                  <DownloadArrow />
                  <span className="font-semibold">{downloadLabel(download.url)}</span>
                  <span className="ml-auto border-l border-primary/30 pl-3 text-sm text-primary/80 transition-colors group-hover/card:border-white/40 group-hover/card:text-white/90">
                    {formatVersion(download.version)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* App store */}
      {platform.appStoreLink && (
        <Link
          href={platform.appStoreLink.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex h-14 items-center gap-3 rounded-xl border border-gray-300 px-5 transition-colors hover:border-gray-400 hover:bg-gray-50 ${
            spec || downloads.length > 0 ? "mt-4" : "mt-7"
          }`}
        >
          <Image
            src={`${baseUrl}/${platform.appStoreLink.icon}`}
            width={platform.appStoreLink.iconWidth ?? 120}
            height={platform.appStoreLink.iconHeight ?? 30}
            priority={isPriority}
            fetchPriority={isPriority ? "high" : "auto"}
            className={platform.appStoreLink.iconClassName ?? "h-6 w-auto max-w-full object-contain"}
            alt={platform.appStoreLink.alt}
          />
          <ChevronRight className="ml-auto flex-shrink-0 text-gray-400" />
        </Link>
      )}

      <div className="mt-auto flex flex-wrap items-center justify-between gap-x-6 gap-y-2 pt-7">
        <Link
          href={platform.installationGuideUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-accent"
        >
          Installation guide
          <ChevronRight />
        </Link>

        {release?.copyPath && (
          <CopyPathButton value={release.copyPath} platformName={platform.name} />
        )}
      </div>
    </article>
  );
}
